import { useEffect, useState } from "react";
import { api, API_URL, inr, fmtDate, formatApiError } from "@/lib/api";
import { FileText, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

const chip = (s) => ({
  Paid: "bg-emerald-100 text-emerald-700", Partial: "bg-amber-100 text-amber-700",
  Overdue: "bg-rose-100 text-rose-700",
}[s] || "bg-slate-100 text-slate-600");

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function PortalInvoices() {
  const [list, setList] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const load = () => api.get("/portal/invoices").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const outstanding = list.reduce((s, i) => s + i.balance, 0);

  const payNow = async (i) => {
    setPayingId(i.id);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Could not load payment gateway — check your connection"); return; }
      const { data: order } = await api.post(`/razorpay/portal/order/${i.id}`);
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Hi-Tech Silico",
        description: `Invoice ${order.invoice_number}`,
        prefill: { name: order.name, email: order.email, contact: order.contact },
        theme: { color: "#3B82F6" },
        handler: async (res) => {
          try {
            const { data } = await api.post("/razorpay/portal/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(data.invoice_status === "Paid"
              ? `Invoice ${order.invoice_number} fully paid — thank you!`
              : "Payment received — thank you!");
            load();
          } catch (e) { toast.error(formatApiError(e)); }
        },
        modal: { ondismiss: () => setPayingId(null) },
      });
      rzp.on("payment.failed", (res) => {
        toast.error(res?.error?.description || "Payment failed — please try again");
        setPayingId(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="portal-invoices">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-900 tracking-tight">Invoices & Payments</h1>
        <div className={`text-sm font-semibold tabular-nums ${outstanding > 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="portal-outstanding">
          Outstanding: {inr(outstanding)}</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-[13px] table-fixed min-w-[920px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider text-left">
            <tr>
              <th className="px-4 py-3 w-[13%]">Invoice No.</th><th className="px-4 py-3 w-[11%]">Date</th>
              <th className="px-4 py-3 w-[11%]">Due Date</th><th className="px-4 py-3 text-right w-[11%]">Amount</th>
              <th className="px-4 py-3 text-right w-[11%]">Received</th><th className="px-4 py-3 text-right w-[11%]">Balance</th>
              <th className="px-4 py-3 text-center w-[11%]">Status</th><th className="px-4 py-3 text-center w-[21%]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{i.number}</td>
                <td className="px-4 py-3">{fmtDate(i.date)}</td>
                <td className="px-4 py-3">{i.due_date ? fmtDate(i.due_date) : "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(i.total)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(i.paid)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{inr(i.balance)}</td>
                <td className="px-4 py-3 text-center"><span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${chip(i.status)}`}>{i.status}</span></td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    {i.balance > 0 && !["Paid", "Returned"].includes(i.status) && (
                      <button onClick={() => payNow(i)} disabled={payingId === i.id}
                              className="inline-flex items-center gap-1 text-xs rounded-md px-3 py-1.5 bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 transition-colors duration-150"
                              data-testid={`pi-pay-${i.id}`}>
                        {payingId === i.id ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />} Pay Now
                      </button>
                    )}
                    <a href={`${API_URL}/portal/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-md px-3 py-1.5 text-slate-600 hover:border-slate-400" title="Download PDF" data-testid={`pi-pdf-${i.id}`}><FileText size={12} /> Invoice</a>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-sm text-slate-400">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Online payments are processed securely via Razorpay (UPI, cards, netbanking). Your invoice updates instantly after payment.</p>
    </div>
  );
}
